import React, { useEffect, useRef, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Paginador } from './HistorialPreguntas';
import './consultas_ia.css';
import './actividad_nawi_chat.css';

// Pestaña "Actividad" del usuario de NAWI Chat: como se reparten en el tiempo las preguntas de sus
// charlas activas, cuantas hizo por cada persona de su panel y, solo para el superadmin, el texto de
// cada pregunta. Todo es de solo lectura.
const GET_ACTIVIDAD = gql`
  query GetNawiChatUserActividad($id: ID!, $semanas: Int) {
    getNawiChatUserActividad(id: $id, semanas: $semanas) {
      semanasVentana desde preguntasTotal preguntasActivas preguntasVentana chatsTotal chatsActivosTotal puedeVerPreguntas
      semanas { inicio preguntas }
      chatsActivos { id titulo persona personaClave enPanel creada primera ultima preguntas porSemana }
      porPersona { clave nombre email tipo chats chatsActivos preguntas preguntasActivas ultima }
      fueraDelPanel { clave nombre email tipo chats chatsActivos preguntas preguntasActivas ultima }
    }
  }
`;
const GET_PREGUNTAS = gql`
  query GetNawiChatUserPreguntas($id: ID!, $pagina: Int, $porPagina: Int, $chatId: ID, $persona: String) {
    getNawiChatUserPreguntas(id: $id, pagina: $pagina, porPagina: $porPagina, chatId: $chatId, persona: $persona) {
      total pagina paginas porPagina
      items { id chatId chatTitulo chatActivo persona personaClave fecha texto cortada estado }
    }
  }
`;

const TZ = 'America/Argentina/Buenos_Aires';
const POR_PAGINA = 10;
const LARGO_CORTO = 420;
const VENTANAS = [[4, 'Últimas 4 semanas'], [12, 'Últimas 12 semanas'], [26, 'Últimos 6 meses'], [52, 'Último año']];
const TIPOS = { email: 'Ya no está en su panel', equipo: 'Equipo', perfil: 'Perfil sin email' };
const TIPO_CHARLA = { email: 'Fuera del panel', equipo: 'Equipo', perfil: 'Perfil sin email' };
const tipoDeClave = (clave) => (clave && clave.includes(':') ? clave.slice(0, clave.indexOf(':')) : clave);
const ESTADOS = {
  blocked: ['tag--yellow', 'Rechazada por políticas'],
  error: ['tag--red', 'Falló la respuesta'],
  partial: ['tag--yellow', 'Respuesta cortada'],
  sin_respuesta: ['tag--gray', 'Sin respuesta'],
};

const num = (v) => new Intl.NumberFormat('es-AR').format(v || 0);
const plural = (v, uno, varios) => `${num(v)} ${v === 1 ? uno : varios}`;
const fechaHora = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const dia = (iso) => (iso ? new Date(iso).toLocaleDateString('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: '2-digit' }) : '');
const diaCorto = (ymd) => (ymd ? `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}` : '');

// Cuanto duro una charla entre su primera y su ultima pregunta.
function duracion(desde, hasta) {
  if (!desde || !hasta) return '';
  const dias = (new Date(hasta) - new Date(desde)) / 86400000;
  if (dias < 1) return 'en un mismo día';
  if (dias < 14) return `a lo largo de ${plural(Math.round(dias), 'día', 'días')}`;
  if (dias < 60) return `a lo largo de ${plural(Math.round(dias / 7), 'semana', 'semanas')}`;
  return `a lo largo de ${plural(Math.round(dias / 30), 'mes', 'meses')}`;
}

// Paso "redondo" para el eje: 1, 2, 5, 10, 20, 50...
function pasoRedondo(max) {
  const crudo = Math.max(1, max / 4);
  const base = 10 ** Math.floor(Math.log10(crudo));
  return [1, 2, 5, 10].map((m) => m * base).find((v) => v >= crudo);
}

// Columna con el extremo de datos redondeado y la base recta.
function columna(x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

function GraficoSemanas({ semanas }) {
  const [activa, setActiva] = useState(null);
  // El grafico toma el ancho real de su caja: asi el texto conserva su tamaño tambien en el celular.
  const caja = useRef(null);
  const [W, setW] = useState(680);
  useEffect(() => {
    const el = caja.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 210;
  const M = { t: 20, r: 6, b: 28, l: 34 };
  const n = semanas.length;
  const max = Math.max(1, ...semanas.map((s) => s.preguntas));
  const paso = pasoRedondo(max);
  const tope = Math.ceil(max / paso) * paso;
  const ticks = [];
  for (let v = 0; v <= tope; v += paso) ticks.push(v);
  const ancho = (W - M.l - M.r) / n;
  const barra = Math.min(24, ancho * 0.62);
  const y = (v) => M.t + (H - M.t - M.b) * (1 - v / tope);
  const iMax = semanas.reduce((im, s, i) => (s.preguntas > semanas[im].preguntas ? i : im), 0);
  const cada = Math.max(1, Math.ceil(40 / ancho));   // cada fecha del eje necesita unos 40 px
  const s = activa === null ? null : semanas[activa];
  const izquierda = activa === null ? 0 : Math.min(88, Math.max(12, ((M.l + (activa + 0.5) * ancho) / W) * 100));
  return (
    <div className="anc-grafico" ref={caja}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={`Preguntas por semana en charlas activas, últimas ${n} semanas`}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={M.l} x2={W - M.r} y1={y(v)} y2={y(v)} className={v === 0 ? 'anc-base' : 'anc-grilla'} />
            <text x={M.l - 7} y={y(v)} className="anc-eje" textAnchor="end" dominantBaseline="middle">{num(v)}</text>
          </g>
        ))}
        {semanas.map((sem, i) => {
          const x = M.l + i * ancho + (ancho - barra) / 2;
          return (
            <g key={sem.inicio}>
              {sem.preguntas > 0 && (
                <path d={columna(x, y(sem.preguntas), barra, y(0) - y(sem.preguntas), 4)} className={`anc-barra${activa === i ? ' anc-barra--activa' : ''}`} />
              )}
              {i === iMax && sem.preguntas > 0 && (
                <text x={x + barra / 2} y={y(sem.preguntas) - 6} textAnchor="middle" className="anc-valor">{num(sem.preguntas)}</text>
              )}
              {i % cada === 0 && <text x={x + barra / 2} y={H - M.b + 17} textAnchor="middle" className="anc-eje">{diaCorto(sem.inicio)}</text>}
              <rect
                x={M.l + i * ancho} y={M.t} width={ancho} height={H - M.t - M.b} className="anc-hit" tabIndex={0}
                aria-label={`Semana del ${diaCorto(sem.inicio)}: ${plural(sem.preguntas, 'pregunta', 'preguntas')}`}
                onMouseEnter={() => setActiva(i)} onMouseLeave={() => setActiva(null)}
                onFocus={() => setActiva(i)} onBlur={() => setActiva(null)}
              />
            </g>
          );
        })}
      </svg>
      {s && (
        <div className="anc-tooltip" style={{ left: `${izquierda}%` }} role="status">
          <b>{num(s.preguntas)}</b> {s.preguntas === 1 ? 'pregunta' : 'preguntas'}
          <span>Semana del {diaCorto(s.inicio)}</span>
        </div>
      )}
    </div>
  );
}

// Actividad de una charla en la ventana, semana por semana.
function MiniSemanas({ valores, semanas }) {
  const W = 120;
  const H = 26;
  const n = valores.length;
  const max = Math.max(1, ...valores);
  const ancho = W / n;
  const barra = Math.max(2, Math.min(7, ancho - 2));
  const total = valores.reduce((a, b) => a + b, 0);
  return (
    <svg className="anc-mini" viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img"
      aria-label={`${plural(total, 'pregunta', 'preguntas')} en las últimas ${n} semanas`}>
      <line x1="0" x2={W} y1={H - 0.5} y2={H - 0.5} className="anc-base" />
      {valores.map((v, i) => {
        if (!v) return null;
        const h = Math.max(2, (H - 3) * (v / max));
        const x = i * ancho + (ancho - barra) / 2;
        return (
          <path key={i} d={columna(x, H - 1 - h, barra, h, 1.5)} className="anc-barra">
            <title>{`Semana del ${diaCorto(semanas[i]?.inicio)}: ${plural(v, 'pregunta', 'preguntas')}`}</title>
          </path>
        );
      })}
    </svg>
  );
}

function FilaPersona({ p, max, elegida, onElegir }) {
  const pct = max ? (p.preguntas / max) * 100 : 0;
  const contenido = (
    <>
      <span className="anc-persona-nombre">
        {p.nombre}
        <small>{p.clave.startsWith('player:')
          ? 'Sin email en su ficha: no se puede cruzar con sus charlas'
          : [p.email && p.email !== p.nombre ? p.email : null, TIPOS[p.tipo]].filter(Boolean).join(' · ')}</small>
      </span>
      <span className="anc-persona-barra" aria-hidden="true">{p.preguntas > 0 && <span style={{ width: `${Math.max(pct, 1.5)}%` }} />}</span>
      <span className="anc-persona-valor">
        <b>{num(p.preguntas)}</b>
        <small>{p.chats ? `${plural(p.chats, 'charla', 'charlas')}, ${num(p.chatsActivos)} ${p.chatsActivos === 1 ? 'activa' : 'activas'}` : 'Sin charlas'}</small>
      </span>
    </>
  );
  const clase = `anc-persona${p.preguntas ? '' : ' anc-persona--cero'}${elegida ? ' anc-persona--elegida' : ''}`;
  if (!onElegir || !p.preguntas) return <div className={clase}>{contenido}</div>;
  return (
    <button type="button" className={clase} aria-pressed={elegida} onClick={onElegir}
      title={`Ver las preguntas sobre ${p.nombre}`}>{contenido}</button>
  );
}

function Pregunta({ it }) {
  const [abierta, setAbierta] = useState(false);
  const larga = (it.texto || '').length > LARGO_CORTO;
  const [clase, texto] = ESTADOS[it.estado] || [];
  return (
    <article className="anc-pregunta">
      <header className="anc-pregunta-meta">
        <span className="anc-pregunta-fecha">{fechaHora(it.fecha) || 'Sin fecha'}</span>
        <span className="tag tag--blue">{it.persona}</span>
        {clase && <span className={`tag ${clase}`}>{texto}</span>}
        {!it.chatActivo && <span className="tag tag--gray">Charla archivada</span>}
      </header>
      <p className="anc-pregunta-chat">Charla: {it.chatTitulo}</p>
      <p className={`anc-pregunta-texto${larga && !abierta ? ' anc-pregunta-texto--corta' : ''}`}>{it.texto}</p>
      {larga && (
        <button type="button" className="btn-subtle anc-ver-mas" onClick={() => setAbierta(!abierta)}>
          {abierta ? 'Ver menos' : 'Ver la pregunta completa'}
        </button>
      )}
      {abierta && it.cortada && <p className="anc-nota">La pregunta es más larga: acá se muestran los primeros 2.000 caracteres.</p>}
    </article>
  );
}

function ListaPreguntas({ userId, filtro, onQuitarFiltro }) {
  const [pagina, setPagina] = useState(1);
  useEffect(() => { setPagina(1); }, [filtro?.tipo, filtro?.clave]);
  const { data, previousData, loading, error } = useQuery(GET_PREGUNTAS, {
    variables: {
      id: userId, pagina, porPagina: POR_PAGINA,
      chatId: filtro?.tipo === 'chat' ? filtro.clave : null,
      persona: filtro?.tipo === 'persona' ? filtro.clave : null,
    },
    fetchPolicy: 'network-only',
  });
  const r = (data || previousData)?.getNawiChatUserPreguntas;
  const desde = r && r.total ? (r.pagina - 1) * r.porPagina + 1 : 0;
  const hasta = r ? desde + r.items.length - 1 : 0;
  return (
    <section className="anc-seccion">
      <h3 className="anc-titulo">Preguntas que hizo</h3>
      <div className="anc-lista-barra">
        <span className="anc-resumen">
          {r ? (r.total ? <>Preguntas <b>{desde}</b> a <b>{hasta}</b> de <b>{num(r.total)}</b>, de la más nueva a la más vieja</> : 'No hay preguntas para mostrar') : ' '}
        </span>
        {filtro && (
          <span className="tag tag--blue anc-filtro">
            {filtro.tipo === 'chat' ? 'Charla' : 'Persona'}: {filtro.etiqueta}
            <button type="button" onClick={onQuitarFiltro} aria-label="Quitar el filtro">×</button>
          </span>
        )}
      </div>
      {error && <div className="cs-aviso cs-aviso--error">{error.message}</div>}
      {!r && loading && <div className="spinner" />}
      {r && r.items.length > 0 && (
        <div className="anc-lista" style={{ opacity: loading ? 0.55 : 1 }}>
          <Paginador pagina={r.pagina} paginas={r.paginas} ir={setPagina} />
          {r.items.map((it) => <Pregunta key={it.id} it={it} />)}
          <Paginador pagina={r.pagina} paginas={r.paginas} ir={setPagina} />
        </div>
      )}
      <p className="anc-nota">Cada vez que se abre esta lista queda anotado en el registro de actividad de Office.</p>
    </section>
  );
}

export default function ActividadNawiChat({ userId }) {
  const [semanas, setSemanas] = useState(12);
  const [filtro, setFiltro] = useState(null);   // { tipo: 'chat' | 'persona', clave, etiqueta }
  const { data, previousData, loading, error } = useQuery(GET_ACTIVIDAD, {
    variables: { id: userId, semanas },
    fetchPolicy: 'network-only',
  });
  const a = (data || previousData)?.getNawiChatUserActividad;
  if (error && !a) return <div className="cs-aviso cs-aviso--error">{error.message}</div>;
  if (!a) return <div className="spinner" />;

  const puede = a.puedeVerPreguntas;
  const elegir = (f) => setFiltro((actual) => (actual && actual.tipo === f.tipo && actual.clave === f.clave ? null : f));
  const maxPersona = Math.max(0, ...a.porPersona.map((p) => p.preguntas), ...a.fueraDelPanel.map((p) => p.preguntas));
  const sinPreguntasEnPanel = a.porPersona.filter((p) => !p.preguntas).length;

  return (
    <div className="anc" style={{ opacity: loading ? 0.6 : 1 }}>
      <div className="anc-filtros">
        <select value={semanas} onChange={(e) => setSemanas(Number(e.target.value))} aria-label="Período">
          {VENTANAS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
        <span className="anc-resumen">
          <b>{num(a.preguntasVentana)}</b> {a.preguntasVentana === 1 ? 'pregunta' : 'preguntas'} en charlas activas desde el lunes {diaCorto(a.desde)}
          {' · '}{plural(a.chatsActivosTotal, 'charla activa', 'charlas activas')} de {num(a.chatsTotal)}
        </span>
      </div>

      <section className="anc-seccion">
        <h3 className="anc-titulo">Preguntas por semana en charlas activas</h3>
        {a.preguntasActivas === 0 ? (
          <div className="empty-state">No hay preguntas en charlas activas.</div>
        ) : (
          <>
            <GraficoSemanas semanas={a.semanas} />
            <details className="anc-tabla-semanas">
              <summary>Ver como tabla</summary>
              <table>
                <thead><tr><th>Semana del</th><th className="anc-num">Preguntas</th></tr></thead>
                <tbody>{a.semanas.map((s) => <tr key={s.inicio}><td>{diaCorto(s.inicio)}/{s.inicio.slice(0, 4)}</td><td className="anc-num">{num(s.preguntas)}</td></tr>)}</tbody>
              </table>
            </details>
          </>
        )}
      </section>

      <section className="anc-seccion">
        <h3 className="anc-titulo">
          Charlas activas
          {a.chatsActivosTotal > a.chatsActivos.length && <small> · las {a.chatsActivos.length} con actividad más reciente de {num(a.chatsActivosTotal)}</small>}
        </h3>
        {a.chatsActivos.length === 0 ? <div className="empty-state">No tiene charlas activas.</div> : (
          <div className="anc-scroll">
            <table className="anc-chats">
              <thead>
                <tr>
                  <th>Charla</th>
                  <th>Persona</th>
                  <th>Semana a semana</th>
                  <th>Primera y última pregunta</th>
                  <th className="anc-num">Preguntas</th>
                </tr>
              </thead>
              <tbody>
                {a.chatsActivos.map((c) => {
                  const elegida = filtro?.tipo === 'chat' && filtro.clave === c.id;
                  const clickable = puede && c.preguntas > 0;
                  const abrir = () => clickable && elegir({ tipo: 'chat', clave: c.id, etiqueta: c.titulo });
                  return (
                    <tr key={c.id} className={`${clickable ? 'anc-fila--clic' : ''}${elegida ? ' anc-fila--elegida' : ''}`}
                      onClick={abrir} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(); } }}
                      tabIndex={clickable ? 0 : undefined} aria-selected={clickable ? elegida : undefined}>
                      <td className="anc-chat-titulo">{c.titulo}</td>
                      <td>{c.persona}{!c.enPanel && TIPO_CHARLA[tipoDeClave(c.personaClave)] && <small className="anc-sub">{TIPO_CHARLA[tipoDeClave(c.personaClave)]}</small>}</td>
                      <td><MiniSemanas valores={c.porSemana} semanas={a.semanas} /></td>
                      <td>
                        {c.primera ? <>{dia(c.primera)} → {dia(c.ultima)}<small className="anc-sub">{duracion(c.primera, c.ultima)}</small></> : <span className="anc-sub">Sin preguntas</span>}
                      </td>
                      <td className="anc-num"><b>{num(c.preguntas)}</b></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="anc-seccion">
        <h3 className="anc-titulo">Preguntas por persona de su panel</h3>
        <p className="anc-nota">Todas sus charlas, activas y archivadas.{sinPreguntasEnPanel ? ` ${plural(sinPreguntasEnPanel, 'persona del panel no tiene', 'personas del panel no tienen')} preguntas.` : ''}</p>
        {a.porPersona.length === 0 ? <div className="empty-state">No tiene personas activas en su panel.</div> : (
          <div className="anc-personas">
            {a.porPersona.map((p) => (
              <FilaPersona key={p.clave} p={p} max={maxPersona}
                elegida={filtro?.tipo === 'persona' && filtro.clave === p.clave}
                onElegir={puede ? () => elegir({ tipo: 'persona', clave: p.clave, etiqueta: p.nombre }) : null} />
            ))}
          </div>
        )}
        {a.fueraDelPanel.length > 0 && (
          <>
            <h4 className="anc-subtitulo">Fuera de su panel</h4>
            <div className="anc-personas">
              {a.fueraDelPanel.map((p) => (
                <FilaPersona key={p.clave} p={p} max={maxPersona}
                  elegida={filtro?.tipo === 'persona' && filtro.clave === p.clave}
                  onElegir={puede ? () => elegir({ tipo: 'persona', clave: p.clave, etiqueta: p.nombre }) : null} />
              ))}
            </div>
          </>
        )}
      </section>

      {puede
        ? <ListaPreguntas userId={userId} filtro={filtro} onQuitarFiltro={() => setFiltro(null)} />
        : <p className="anc-nota anc-solo-super">El texto de las preguntas solo lo ve el superadmin.</p>}
    </div>
  );
}
